import { CreatePricingSettingsError } from './pricingService'
import { MessageKind, Message } from 'uiComponents/messages'
import { AvailabilityDates } from 'products/articleConfigurationService'
import { SingleSelectOption } from 'uiComponents/input'
import { t } from 'translations'

export const getPriceStepSizeOptions = (): SingleSelectOption[] => [
    { name: t('products.pricing.common.stepSizeNoRounding'), value: '0.01' },
    { name: '0.05', value: '0.05' },
    { name: '0.10', value: '0.10' },
    { name: '0.25', value: '0.25' },
    { name: '0.50', value: '0.50' },
    { name: '1.00', value: '1.00' },
]

export const DEFAULT_STEP_SIZE = '0.01'

export const getPriceStepSizeInfotipText = () => t('products.pricing.common.stepSizeInfotip')

export const getCharmPricingInfotipText = () => t('products.pricing.common.charmPricingInfotip')

export const getPricingSaveSuccessMessage = (): Message => ({
    id: 'pricing_settings_save_success',
    status: 'success',
    text: t('products.pricing.common.saveSuccess'),
    visible: true,
})

export function handleFailure(
    err: CreatePricingSettingsError,
    replaceTopMessages: (id: string, status: MessageKind, text: string) => void,
) {
    switch (err.type) {
        case 'server_error':
            replaceTopMessages('unknown_error', 'error', t('products.pricing.errors.somethingWentWrongLater'))
            return
        case 'settings_validation':
            replaceTopMessages(
                'settings_validation',
                'error',
                t('products.pricing.common.settingsValidationError', { message: err.message }),
            )
            return
        case 'override_validation':
            replaceTopMessages(
                'override_validation',
                'error',
                t('products.pricing.common.overrideValidationError', {
                    overrideName: err.overrideName,
                    message: err.message,
                }),
            )
            return
        case 'unknown_validation_error':
            replaceTopMessages(
                'unknown_validation_error',
                'error',
                t('products.pricing.exceptions.form.errorWithMessage', { message: err.message }),
            )
            return
        default:
            throw new Error(`Unknown error type ${(err as any).type}`)
    }
}

export interface Validity {
    valid: boolean
    message: string
}
export interface Validities {
    [key: string]: Validity
}
export function getValidity(value: number | null): {
    valid: boolean
    message: string
} {
    let validity = {
        valid: true,
        message: '',
    }
    if (value === null) {
        validity = {
            valid: false,
            message: t('products.pricing.common.validation.priceRequired'),
        }
    } else if (value < 0) {
        validity = {
            valid: false,
            message: t('products.pricing.common.validation.priceMin'),
        }
    } else if (value > 9999) {
        validity = {
            valid: false,
            message: t('products.pricing.common.validation.priceMax'),
        }
    }
    return validity
}

export function priceToNumberOrNull(price: string) {
    return price === null ? null : Number(price)
}

export const validateMinAcceptancePrice = (min: number | null, max: number | null) => {
    let validity = {
        valid: true,
        message: '',
    }
    if (min === null) {
        return validity
    } else if (max !== null && min > max) {
        validity = {
            valid: false,
            message: t('products.pricing.common.validation.minAboveMax'),
        }
    } else if (min < 0) {
        validity = {
            valid: false,
            message: t('products.pricing.common.validation.minNegative'),
        }
    } else if (min > 9999) {
        validity = {
            valid: false,
            message: t('products.pricing.common.validation.priceMax'),
        }
    }
    return validity
}

export const validateAvgTargetPrice = (avg: number | null, min: number | null, max: number | null) => {
    let validity = {
        valid: true,
        message: '',
    }
    if (avg === null) {
        return validity
    }
    if (max !== null && avg > max) {
        validity = {
            valid: false,
            message: t('products.pricing.common.validation.avgAboveMax'),
        }
    }
    if (min !== null && avg < min) {
        validity = {
            valid: false,
            message: t('products.pricing.common.validation.avgBelowMin'),
        }
    }
    if (avg < 0) {
        validity = {
            valid: false,
            message: t('products.pricing.common.validation.avgNegative'),
        }
    } else if (avg > 9999) {
        validity = {
            valid: false,
            message: t('products.pricing.common.validation.priceMax'),
        }
    }
    return validity
}

export const validateGatePrice = (gate: number | null, max: number | null) => {
    let validity = {
        valid: true,
        message: '',
    }
    if (gate === null) {
        return validity
    } else if (max !== null && gate < max) {
        validity = {
            valid: false,
            message: t('products.pricing.common.validation.gateBelowMax'),
        }
    } else if (gate < 0) {
        validity = {
            valid: false,
            message: t('products.pricing.common.validation.gateNegative'),
        }
    } else if (gate > 9999) {
        validity = {
            valid: false,
            message: t('products.pricing.common.validation.priceMax'),
        }
    }
    return validity
}

export function isAnyDateAvailable(data: AvailabilityDates): boolean {
    const dates = Object.keys(data)
    let someDateAvailable = false
    dates.forEach((key) => {
        if (data[key].available) {
            someDateAvailable = true
            return
        }
    })
    return someDateAvailable
}
